import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getUser } from '../../utils/auth';
import { getEvents } from '../../utils/storage';
import { getActiveEventId, setActiveEventId, DEFAULT_EVENT_ID } from '../../utils/event';
import { getAccessibleTestIds } from '../../utils/testAccess';
import type { UserRole, MedicalEvent } from '../../types';

interface SidebarItem {
  label: string;
  icon: string;
  path: string;
  allowedRoles: UserRole[];
  testId?: string; // when set, the item is only shown if the test is enabled for the event + role has access
  children?: SidebarItem[];
}

const sidebarItems: SidebarItem[] = [
  {
    label: 'Dashboard',
    icon: 'ri-dashboard-line',
    path: '/dashboard',
    allowedRoles: ['master_admin', 'admin', 'patient_registration', 'bp', 'heart_rate', 'spo2', 'temperature', 'rbs', 'cholesterol', 'height_weight', 'blood_grouping', 'colour_blindness', 'visual_acuity', 'patient_monitor', 'view_only', 'all_vitals', 'cashier', 'chest_circumference', 'respiratory'],
  },
  {
    label: 'New Patient Registration',
    icon: 'ri-user-add-line',
    path: '/patient-registration',
    allowedRoles: ['master_admin', 'admin', 'patient_registration'],
  },
  {
    label: 'BP Entry',
    icon: 'ri-heart-pulse-line',
    path: '/bp-entry',
    allowedRoles: ['bp', 'all_vitals', 'admin', 'master_admin'],
    testId: 'bp',
  },
  {
    label: 'Heart Rate Entry',
    icon: 'ri-heart-line',
    path: '/heart-rate-entry',
    allowedRoles: ['heart_rate', 'all_vitals', 'admin', 'master_admin'],
    testId: 'hr',
  },
  {
    label: 'SpO2 Entry',
    icon: 'ri-lungs-line',
    path: '/spo2-entry',
    allowedRoles: ['spo2', 'all_vitals', 'admin', 'master_admin'],
    testId: 'spo2',
  },
  {
    label: 'Temperature Entry',
    icon: 'ri-temp-hot-line',
    path: '/temperature-entry',
    allowedRoles: ['temperature', 'all_vitals', 'admin', 'master_admin'],
    testId: 'temp',
  },
  {
    label: 'RBS Entry',
    icon: 'ri-drop-line',
    path: '/rbs-entry',
    allowedRoles: ['rbs', 'all_vitals', 'admin', 'master_admin'],
    testId: 'rbs',
  },
  {
    label: 'Cholesterol Entry',
    icon: 'ri-test-tube-line',
    path: '/cholesterol-entry',
    allowedRoles: ['cholesterol', 'all_vitals', 'admin', 'master_admin'],
    testId: 'chol',
  },
  {
    label: 'Height & Weight Entry',
    icon: 'ri-ruler-line',
    path: '/height-weight-entry',
    allowedRoles: ['height_weight', 'all_vitals', 'admin', 'master_admin'],
    testId: 'height_weight',
  },
  {
    label: 'Blood Grouping Entry',
    icon: 'ri-contrast-drop-line',
    path: '/blood-grouping-entry',
    allowedRoles: ['blood_grouping', 'all_vitals', 'admin', 'master_admin'],
    testId: 'blood_grouping',
  },
  {
    label: 'Colour Blindness Entry',
    icon: 'ri-palette-line',
    path: '/colour-blindness-entry',
    allowedRoles: ['colour_blindness', 'all_vitals', 'admin', 'master_admin'],
    testId: 'colour_blindness',
  },
  {
    label: 'Visual Acuity Entry',
    icon: 'ri-eye-line',
    path: '/visual-acuity-entry',
    allowedRoles: ['visual_acuity', 'all_vitals', 'admin', 'master_admin'],
    testId: 'visual_acuity',
  },
  {
    label: 'Body Fat Entry',
    icon: 'ri-body-scan-line',
    path: '/body-fat-entry',
    allowedRoles: ['body_fat', 'all_vitals', 'admin', 'master_admin'],
    testId: 'body_fat',
  },
  {
    label: 'Chest Circumference Entry',
    icon: 'ri-ruler-2-line',
    path: '/chest-circumference-entry',
    allowedRoles: ['chest_circumference', 'admin', 'master_admin'],
    testId: 'chest_circ',
  },
  {
    label: 'Respiratory Tests Entry',
    icon: 'ri-lungs-line',
    path: '/respiratory-entry',
    allowedRoles: ['respiratory', 'admin', 'master_admin'],
    testId: 'respiratory',
  },
  {
    label: 'Patient Monitor Vitals',
    icon: 'ri-stethoscope-line',
    path: '/patient-monitor-vitals',
    allowedRoles: ['patient_monitor'],
    testId: 'patient_monitor',
  },
  {
    label: 'ECG Entry',
    icon: 'ri-pulse-line',
    path: '/ecg-entry',
    allowedRoles: ['admin', 'master_admin', 'all_vitals'],
    testId: 'ecg',
  },
  {
    label: 'All Vitals Entry',
    icon: 'ri-stethoscope-line',
    path: '/vitals-entry',
    allowedRoles: ['all_vitals', 'admin', 'master_admin'],
  },
  {
    label: 'View Vitals',
    icon: 'ri-eye-line',
    path: '/view-vitals',
    allowedRoles: ['view_only', 'admin', 'master_admin', 'all_vitals'],
  },
  {
    label: 'Analytics',
    icon: 'ri-bar-chart-2-line',
    path: '/analytics',
    allowedRoles: ['admin', 'master_admin'],
  },
  {
    label: 'Accounts',
    icon: 'ri-money-rupee-circle-line',
    path: '/accounts',
    allowedRoles: ['master_admin', 'admin'],
    children: [
      { label: 'Dashboard', icon: 'ri-pie-chart-2-line', path: '/accounts/dashboard', allowedRoles: ['master_admin', 'admin'] },
      { label: 'Collect Fee', icon: 'ri-hand-coin-line', path: '/accounts/collect-fee', allowedRoles: ['master_admin', 'admin'] },
      { label: 'Revenue', icon: 'ri-line-chart-line', path: '/accounts/revenue', allowedRoles: ['master_admin', 'admin'] },
      { label: 'Expenses', icon: 'ri-file-list-line', path: '/accounts/expenses', allowedRoles: ['master_admin', 'admin'] },
      { label: 'Net Profit', icon: 'ri-funds-line', path: '/accounts/net-profit', allowedRoles: ['master_admin', 'admin'] },
      { label: 'Bank Settings', icon: 'ri-bank-line', path: '/accounts/bank-settings', allowedRoles: ['master_admin', 'admin'] },
    ],
  },
  { label: 'Collect Fee', icon: 'ri-hand-coin-line', path: '/accounts/collect-fee', allowedRoles: ['cashier'] },
  { label: 'Edit Requests', icon: 'ri-edit-box-line', path: '/edit-requests', allowedRoles: ['admin', 'master_admin'] },
  { label: 'User Management', icon: 'ri-team-line', path: '/user-management', allowedRoles: ['master_admin'] },
  { label: 'Test Master', icon: 'ri-test-tube-line', path: '/test-master', allowedRoles: ['master_admin'] },
  { label: 'Activity Logs', icon: 'ri-file-list-3-line', path: '/activity-logs', allowedRoles: ['master_admin'] },
  { label: 'Audit Logs', icon: 'ri-shield-check-line', path: '/audit-logs', allowedRoles: ['master_admin'] },
  { label: 'Settings', icon: 'ri-settings-3-line', path: '/settings', allowedRoles: ['master_admin'] },
];

const statusStyles: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  draft: 'bg-amber-100 text-amber-700',
  archived: 'bg-gray-100 text-gray-600',
};

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export default function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [events, setEvents] = useState<MedicalEvent[]>([]);
  const [accessibleTests, setAccessibleTests] = useState<string[]>([]);
  const activeEventId = getActiveEventId();

  const isMaster = user?.role === 'master_admin';

  useEffect(() => {
    const loadEvents = async () => {
      const all = await getEvents();
      let visible = all;
      if (user && user.role !== 'master_admin') {
        const allowed = user.eventIds || [];
        visible = all.filter((e) => allowed.includes(e.id));
      }
      setEvents(visible);
    };
    loadEvents();
  }, [user]);

  useEffect(() => {
    const loadTests = async () => {
      const ids = await getAccessibleTestIds(user);
      setAccessibleTests(ids);
    };
    loadTests();

    const onEventChanged = () => loadTests();
    window.addEventListener('active-event-changed', onEventChanged);
    return () => window.removeEventListener('active-event-changed', onEventChanged);
  }, [user, activeEventId]);

  const activeEvent = events.find((e) => e.id === activeEventId);
  const currentEventLabel = activeEvent?.eventName || 'All Events';

  const filteredItems = sidebarItems.filter((item) => {
    if (!user) return false;
    if (!item.allowedRoles.includes(user.role)) return false;
    // Tests where the role doesn't obviously map: default to dashboard-level base-role check.
    // Non-admin users see a test entry only if it is enabled for the event and their role may access it.
    if (item.testId && user.role !== 'admin' && user.role !== 'master_admin' && user.role !== 'all_vitals') {
      return accessibleTests.includes(item.testId);
    }
    return true;
  });

  const handleSwitchEvent = (eventId: string) => {
    setActiveEventId(eventId);
    navigate('/dashboard');
  };

  return (
    <aside
      className={`fixed left-0 top-16 h-[calc(100vh-64px)] bg-white border-r border-gray-200 z-40 transition-all duration-300 ${
        collapsed ? 'w-[68px]' : 'w-60'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="p-3">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center h-9 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-gray-500 text-lg`}></i>
          </button>
        </div>

        {isMaster && (
          <div className="px-3 pb-2 border-b border-gray-100 mb-2">
            <div className="px-2 py-1 flex items-center gap-2 mb-1">
              <i className="ri-shield-star-line text-teal-600 text-base"></i>
              {!collapsed && (
                <span className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">Master Admin</span>
              )}
            </div>

            <button
              onClick={() => navigate('/events')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap mb-1 ${
                location.pathname === '/events'
                  ? 'bg-teal-50 text-teal-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }`}
              title={collapsed ? 'Events' : undefined}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-stack-line text-lg"></i>
              </div>
              {!collapsed && <span>Events</span>}
            </button>

            {!collapsed && (
              <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
                <div className="flex items-center gap-1.5 px-1 mb-1.5">
                  <i className="ri-calendar-event-line text-xs text-teal-600"></i>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Current Event</span>
                </div>
                <select
                  value={activeEventId}
                  onChange={(e) => handleSwitchEvent(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md outline-none cursor-pointer focus:ring-2 focus:ring-teal-500"
                >
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.eventName} ({ev.eventCode})</option>
                  ))}
                  {events.length === 0 && (
                    <option value={DEFAULT_EVENT_ID}>Medsparkz</option>
                  )}
                </select>
                {activeEvent && (
                  <div className="mt-1.5 px-1 flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize ${statusStyles[activeEvent.status] || 'bg-gray-100 text-gray-600'}`}>
                      {activeEvent.status}
                    </span>
                    <button
                      onClick={() => navigate('/events')}
                      className="text-[10px] text-teal-600 hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Manage
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* EVENT context banner for non-master users */}
        {!isMaster && !collapsed && (
          <div className="px-3 pb-2 mb-2">
            <div className="rounded-lg bg-teal-50 border border-teal-100 px-3 py-2 flex items-center gap-2">
              <i className="ri-calendar-event-line text-teal-600 text-sm"></i>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-500">Event</p>
                <p className="text-sm font-semibold text-teal-800 truncate">
                  {activeEvent?.eventCode || activeEventId.toUpperCase()}
                </p>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {filteredItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const hasChildren = item.children && item.children.length > 0;

            return (
              <div key={item.path}>
                <button
                  onClick={() => !hasChildren && navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  {!collapsed && <span>{item.label}</span>}
                </button>

                {hasChildren && !collapsed && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.children?.filter(child => user && child.allowedRoles.includes(user.role)).map((child) => {
                      const isChildActive = location.pathname === child.path;
                      return (
                        <button
                          key={child.path}
                          onClick={() => navigate(child.path)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            isChildActive
                              ? 'bg-teal-50 text-teal-700'
                              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          }`}
                        >
                          <i className={`${child.icon} text-sm`}></i>
                          <span>{child.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-100">
          {!collapsed && user && (
            <div className="px-3 py-2">
              <p className="text-xs text-gray-400 truncate">Logged in as</p>
              <p className="text-sm font-medium text-gray-700 truncate">{user.displayName || user.username}</p>
              <p className="text-xs text-gray-400 mt-0.5 capitalize truncate">{user.role.replace(/_/g, ' ')}</p>
              {!isMaster && (
                <div className="mt-2 px-2 py-1.5 bg-teal-50 rounded-md flex items-center gap-1.5">
                  <i className="ri-calendar-event-line text-xs text-teal-600"></i>
                  <span className="text-[11px] font-medium text-teal-700 truncate">{currentEventLabel}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}